import { useState, useEffect, useMemo, useRef } from 'react';
import { weathercodes } from './Weathercodes';
import { getFestivalLat, getFestivalLng } from '../../config/helpers';

// Same 8-way icon categorisation FestivalDate.js already uses (via
// Weathercodes.js) for its own live forecast, just mapped to an emoji here
// instead of the PNG image WeatherIcon.js renders.
const EMOJI_BY_CATEGORY = {
  sun: '☀️',
  suncloud: '⛅',
  cloud: '☁️',
  rain: '🌧️',
  cold: '❄️',
  storm: '⛈️',
  wind: '💨',
};

export const emojiForCode = (code) => {
  const match = weathercodes.find(w => w.code === code);
  return EMOJI_BY_CATEGORY[match?.icon] || '⛅';
};

export const inferNoteEmoji = (note) => {
  const t = (note || '').toLowerCase();
  if (/hot|sunny|sun/.test(t)) return '☀️';
  if (/rain|storm|wet/.test(t)) return '🌧️';
  if (/cold|cool|chilly|frost/.test(t)) return '🧊';
  if (/wind/.test(t)) return '💨';
  return '⛅';
};

const MAX_TILES = 3;

// Root.js fully unmounts Home (`{currentScreen === "home" && <Home .../>}`)
// on every navigation away from it, so without caching this hook re-fetches
// from scratch on every single visit — not just the first — showing the
// same 1-2s pop-in each time. localStorage survives that remount (and full
// page reloads), so a repeat visit renders with data already in hand.
// Climate keys never go stale (a historical average for a fixed lat/lng/
// month never changes); live-forecast keys fold in today's date so a new
// day naturally invalidates yesterday's cached forecast.
const readWeatherCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeWeatherCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Storage disabled/full (e.g. Safari private mode) — just skip caching.
  }
};

// All of WeatherCard's data-fetching (admin override detection, live
// forecast, historical climate average), lifted out so both the collapsed
// weather chip and the expanded WeatherCard can share one fetch instead of
// each running its own — the two are shown together on Home and would
// otherwise hit the same APIs twice for the same festival.
export const useWeatherData = (festivalInfo) => {
  const location = festivalInfo?.location;
  const lat = getFestivalLat(festivalInfo);
  const lng = getFestivalLng(festivalInfo);
  const startdate = festivalInfo?.startdate;
  const enddate = festivalInfo?.enddate;
  const weatherNote = festivalInfo?.weatherNote;

  const daysToStart = useMemo(() => {
    if (!startdate) return null;
    const now = new Date();
    const start = new Date(startdate);
    return Math.ceil((start - now) / (1000 * 60 * 60 * 24));
  }, [startdate]);

  // Priority: admin override > live forecast (within 7 days) > climate average.
  // daysToStart needs a lower bound too — a festival whose start date has
  // long since passed still satisfies `<= 7` (any negative number does),
  // which would route it into the live-forecast path even though
  // weatherapi.com has no forecast data for a date years in the past.
  const showOverride = !!weatherNote && weatherNote.trim().length > 0;
  // "Within 7 days" is only a candidate for live data, not a guarantee one
  // exists — the weatherapi.com key here is on a plan that only returns 3
  // days of forecast from today, so a festival 4-7 days out (or missing a
  // location string) will never find a matching day and forecastFailed
  // flips true below. showLive/showClimate react to that so the widget
  // falls back to the climate average instead of permanently hiding.
  const wantsLive = !showOverride && daysToStart !== null && daysToStart >= 0 && daysToStart <= 7;

  const liveCacheKey = (location && startdate)
    ? `weather_live_v1_${location}_${startdate}_${enddate || ''}_${new Date().toDateString()}`
    : null;

  const [forecastDays, setForecastDays] = useState(() => {
    if (!liveCacheKey) return null;
    const cached = readWeatherCache(liveCacheKey);
    return cached ? cached.map(d => ({ ...d, date: new Date(d.dateISO) })) : null;
  });
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastFailed, setForecastFailed] = useState(false);

  const showLive = wantsLive && !forecastFailed;
  const showClimate = !showOverride && (!wantsLive || forecastFailed);
  // Guards against a double-fetch — a ref rather than the state above,
  // because React 18 StrictMode double-invokes effects in development
  // (mount -> cleanup -> mount again) purely to surface missing-cleanup
  // bugs. With state (climateData/forecastDays etc.) in the dependency
  // array, the *first* invocation's own setForecastLoading(true) call
  // would already be visible by the time the second ("real") invocation
  // runs, so it would see loading:true and bail out — while the fetch
  // that's actually still in flight belongs to the first invocation,
  // whose cleanup already flipped its own `cancelled` flag, so its result
  // never gets applied either. Net effect: loading gets stuck true
  // forever, silently.
  //
  // Storing the *key* this ref was set for (not just a bare boolean) is
  // what makes switching festivals work without a full page reload. Home
  // stays mounted across a festival switch (Root.js just gives it new
  // props, currentScreen doesn't change), so this ref's state survives too
  // — a bare "have we ever fetched" boolean would stay true forever after
  // the first festival and silently skip fetching every festival after it,
  // leaving the previous one's forecast on screen indefinitely. Comparing
  // against the current liveCacheKey (which bakes in location+dates) means
  // a genuinely new festival is recognised as needing its own fetch, while
  // StrictMode's synchronous double-invoke (same key both times) is still
  // deduped exactly as before.
  const forecastFetchedForKey = useRef(null);

  // Live forecast — reuses the same weatherapi.com endpoint/key
  // FestivalDate.js already fetches from elsewhere in the app; this just
  // also reads temperature, which that existing call discards.
  useEffect(() => {
    if (!wantsLive) return;
    if (!liveCacheKey) {
      setForecastFailed(true);
      return;
    }
    if (forecastFetchedForKey.current === liveCacheKey) return;
    forecastFetchedForKey.current = liveCacheKey;

    // Cache hit for this exact festival+day — no need to fetch.
    const cached = readWeatherCache(liveCacheKey);
    if (cached) {
      setForecastDays(cached.map(d => ({ ...d, date: new Date(d.dateISO) })));
      setForecastFailed(false);
      return;
    }

    // Without a location string or start date there's nothing to query —
    // fail fast so showClimate flips on and the widget falls back, rather
    // than sitting in "loading" forever with a fetch that never starts.
    if (!location || !startdate) {
      setForecastFailed(true);
      return;
    }
    // Clear out whatever the previous festival left behind so the widget
    // doesn't show stale data while this festival's fetch is in flight.
    setForecastDays(null);
    setForecastLoading(true);
    setForecastFailed(false);
    (async () => {
      try {
        const response = await fetch(
          `https://api.weatherapi.com/v1/forecast.json?key=f95e1f28a0fa4c4e89345802213108&q=${encodeURIComponent(location)}&days=10&aqi=no&alerts=no`
        );
        if (!response.ok) throw new Error('forecast fetch failed');
        const resData = await response.json();
        const forecast = resData?.forecast?.forecastday || [];
        const start = new Date(startdate);
        const end = enddate ? new Date(enddate) : start;
        const totalDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
        const todayStart = new Date(new Date().toDateString());

        const days = [];
        for (let i = 0; i < totalDays && days.length < MAX_TILES; i++) {
          const d = new Date(start.getTime() + i * 86400000);
          if (d < todayStart) continue;
          const match = forecast.find(f => f.date === d.toLocaleDateString('en-CA'));
          if (!match) continue;
          days.push({
            date: d,
            temp: Math.round(match.day.avgtemp_c),
            emoji: emojiForCode(match.day.condition.code),
          });
        }
        if (days.length === 0) throw new Error('no matching forecast days');
        setForecastDays(days);
        writeWeatherCache(liveCacheKey, days.map(d => ({ ...d, dateISO: d.date.toISOString(), date: undefined })));
      } catch (err) {
        setForecastFailed(true);
      } finally {
        setForecastLoading(false);
      }
    })();
  }, [wantsLive, location, startdate, enddate, liveCacheKey]);

  const climateCacheKey = (lat && lng && startdate) ? `weather_climate_v1_${lat}_${lng}_${startdate}` : null;

  const [climateData, setClimateData] = useState(() =>
    climateCacheKey ? readWeatherCache(climateCacheKey) : null
  );
  const [climateLoading, setClimateLoading] = useState(false);
  const [climateFailed, setClimateFailed] = useState(false);
  // See forecastFetchedForKey above — same reasoning: keyed on
  // climateCacheKey (not a bare boolean) so switching festivals without a
  // remount is recognised as needing a fresh fetch instead of silently
  // keeping the previous festival's climate average on screen forever.
  const climateFetchedForKey = useRef(null);

  // Historical climate average — Open-Meteo's free archive API, no key
  // required. Same calendar month as the festival, one year prior.
  useEffect(() => {
    if (!showClimate || !climateCacheKey) return;
    if (climateFetchedForKey.current === climateCacheKey) return;
    climateFetchedForKey.current = climateCacheKey;

    const cached = readWeatherCache(climateCacheKey);
    if (cached) {
      setClimateData(cached);
      setClimateFailed(false);
      return;
    }

    // Clear out whatever the previous festival left behind so the widget
    // doesn't show stale data while this festival's fetch is in flight.
    setClimateData(null);
    setClimateLoading(true);
    setClimateFailed(false);
    (async () => {
      try {
        const start = new Date(startdate);
        const year = start.getFullYear() - 1;
        const month = start.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const fmt = (d) => d.toISOString().slice(0, 10);
        const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${fmt(firstDay)}&end_date=${fmt(lastDay)}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=Australia/Sydney`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('climate fetch failed');
        const resData = await response.json();
        const highs = resData?.daily?.temperature_2m_max || [];
        const lows = resData?.daily?.temperature_2m_min || [];
        const rain = resData?.daily?.precipitation_sum || [];
        if (highs.length === 0) throw new Error('no climate data');
        const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
        const result = {
          avgHigh: Math.round(avg(highs)),
          avgLow: Math.round(avg(lows)),
          rainyDays: rain.filter(v => v > 1).length,
          month: start.toLocaleString(undefined, { month: 'long' }),
        };
        setClimateData(result);
        writeWeatherCache(climateCacheKey, result);
      } catch (err) {
        setClimateFailed(true);
      } finally {
        setClimateLoading(false);
      }
    })();
  }, [showClimate, lat, lng, startdate, climateCacheKey]);

  return {
    location, lat, lng, startdate, weatherNote,
    showOverride, showLive, showClimate,
    forecastDays, forecastLoading, forecastFailed,
    climateData, climateLoading, climateFailed,
  };
};

export default useWeatherData;
